class OkazoCollection extends HTMLElement {
  connectedCallback() {
    if (this.connection) return;
    this.connection = new AbortController();
    const { signal } = this.connection;
    this.classList.add("is-enhanced");
    this.dialog = this.querySelector("[data-filter-dialog]");
    this.loading = this.querySelector("[data-collection-loading]");
    this.mobile = window.matchMedia("(max-width: 959px)");
    this.committedURL = location.href;
    this.validatePriceRange();
    this.addEventListener("click", (event) => this.onClick(event), { signal });
    this.addEventListener("change", (event) => this.onChange(event), { signal });
    this.addEventListener(
      "input",
      (event) => {
        if (!event.target.matches('[data-filter-form] input[type="number"]')) return;
        this.validatePriceRange();
        if (this.dialog.open) this.queueCount();
      },
      { signal },
    );
    this.addEventListener(
      "submit",
      (event) => {
        if (!event.target.matches("[data-filter-form]")) return;
        event.preventDefault();
        if (!this.validatePriceRange() || !event.target.reportValidity()) return;
        this.navigate(this.formURL(), { scroll: this.dialog.open });
      },
      { signal },
    );
    this.dialog.addEventListener("close", () => this.restoreForm(), { signal });
    this.mobile.addEventListener(
      "change",
      () => {
        if (!this.mobile.matches && this.dialog.open) this.dialog.close();
      },
      { signal },
    );
    window.addEventListener(
      "popstate",
      () => {
        const current = new URL(this.committedURL);
        if (location.pathname === current.pathname && location.search === current.search) return;
        this.navigate(location.href, { history: false });
      },
      { signal },
    );
  }

  disconnectedCallback() {
    this.request?.abort();
    this.cancelCount();
    this.connection?.abort();
    this.connection = null;
    if (this.dialog?.open) this.dialog.close();
    document.documentElement.classList.remove("okz-filters-open");
  }

  formURL() {
    const form = this.querySelector("[data-filter-form]");
    const url = new URL(form.action);
    const current = new URL(this.committedURL);
    for (const key of ["preview_theme_id", "preview_key"]) {
      if (current.searchParams.has(key)) url.searchParams.set(key, current.searchParams.get(key));
    }
    for (const [key, value] of new FormData(form)) {
      if (String(value).trim()) url.searchParams.append(key, value);
    }
    return url;
  }

  onChange(event) {
    if (event.target.matches("[data-collection-sort]")) {
      if (!this.validatePriceRange() || !event.target.form.reportValidity()) {
        event.target.value = [...event.target.options].find((option) => option.defaultSelected)?.value || "";
        return;
      }
      this.navigate(this.formURL(), { scroll: true });
    } else if (event.target.closest("[data-filter-form]")) {
      if (this.dialog.open) this.queueCount();
      else if (this.validatePriceRange() && event.target.form.reportValidity()) this.navigate(this.formURL());
    }
  }

  validatePriceRange() {
    const minimum = this.querySelector('[name="filter.v.price.gte"]');
    const maximum = this.querySelector('[name="filter.v.price.lte"]');
    const error = this.querySelector("[data-price-error]");
    if (!minimum || !maximum || !error) return true;
    const invalid = minimum.value !== "" && maximum.value !== "" && minimum.valueAsNumber > maximum.valueAsNumber;
    maximum.setCustomValidity(invalid ? error.textContent.trim() : "");
    error.hidden = !invalid;
    for (const input of [minimum, maximum]) {
      if (invalid) input.setAttribute("aria-invalid", "true");
      else input.removeAttribute("aria-invalid");
    }
    return !invalid;
  }

  onClick(event) {
    const more = event.target.closest("[data-filter-more]");
    if (more) {
      const expanded = more.closest(".okz-facet").classList.toggle("is-expanded");
      more.setAttribute("aria-expanded", String(expanded));
      more.textContent = expanded ? more.dataset.less : more.dataset.more;
      return;
    }
    if (event.target.closest("[data-open-filters]")) {
      this.openFilters();
      return;
    }
    if (event.target.closest("[data-close-filters]") || event.target === this.dialog) {
      this.dialog.close();
      return;
    }
    if (event.target.closest("[data-reset-filters]")) {
      this.querySelectorAll('[data-filter-form] input[type="checkbox"]').forEach((input) => {
        input.checked = false;
      });
      this.querySelectorAll('[data-filter-form] input[type="number"]').forEach((input) => {
        input.value = "";
      });
      this.queueCount();
      return;
    }
    const link = event.target.closest("a[data-collection-link],a[data-collection-page]");
    if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    this.navigate(link.href, { scroll: link.hasAttribute("data-collection-page") });
  }

  openFilters() {
    const form = this.querySelector("[data-filter-form]");
    this.pendingValues = [...form.elements].map((input) => ({
      input,
      value: input.value,
      checked: input.checked,
      disabled: input.disabled,
      count: input.type === "checkbox" ? input.closest("label").querySelector("small").textContent : null,
    }));
    this.querySelector("[data-filter-slot]").append(form);
    document.documentElement.classList.add("okz-filters-open");
    this.dialog.showModal();
    this.setCountLabel(this.querySelector("[data-result-count]").dataset.applyCountLabel);
    this.updateReset();
  }

  restoreForm() {
    this.cancelCount();
    const form = this.querySelector("[data-filter-form]");
    if (form) this.querySelector("[data-filter-home]").append(form);
    for (const entry of this.pendingValues || []) {
      entry.input.value = entry.value;
      entry.input.checked = entry.checked;
      entry.input.disabled = entry.disabled;
      if (entry.count !== null) {
        const label = entry.input.closest("label");
        label.querySelector("small").textContent = entry.count;
        label.classList.toggle("is-disabled", entry.disabled);
      }
    }
    form?.removeAttribute("data-count-unavailable");
    this.pendingValues = null;
    this.validatePriceRange();
    document.documentElement.classList.remove("okz-filters-open");
  }

  updateReset() {
    const form = this.querySelector("[data-filter-form]");
    const hasChoices =
      Boolean(form.querySelector('input[type="checkbox"]:checked')) ||
      [...form.querySelectorAll('input[type="number"]')].some((input) => input.value !== "");
    this.querySelector("[data-reset-filters]").disabled = !hasChoices;
  }

  updateFacetCounts(result) {
    const incoming = new Map(
      [...result.querySelectorAll('[data-filter-form] input[type="checkbox"]')].map((input) => [
        JSON.stringify([input.name, input.value]),
        input.closest("label").querySelector("small").textContent,
      ]),
    );
    const form = this.querySelector("[data-filter-form]");
    for (const input of form.querySelectorAll('input[type="checkbox"]')) {
      const count = incoming.get(JSON.stringify([input.name, input.value])) ?? "0";
      const label = input.closest("label");
      label.querySelector("small").textContent = count;
      input.disabled = Number(count.trim()) === 0 && !input.checked;
      label.classList.toggle("is-disabled", input.disabled);
    }
    form.removeAttribute("data-count-unavailable");
  }

  setCountLabel(label, pending = false) {
    const button = this.querySelector("[data-filter-apply]");
    if (!button) return;
    button.querySelector("[data-filter-apply-label]").textContent = label;
    button.setAttribute("aria-busy", String(pending));
  }

  cancelCount() {
    window.clearTimeout(this.countTimer);
    this.countRequest?.abort();
    this.countRequest = null;
  }

  queueCount() {
    this.cancelCount();
    this.updateReset();
    const validPriceRange = this.validatePriceRange();
    const form = this.querySelector("[data-filter-form]");
    form.setAttribute("data-count-unavailable", "");
    for (const input of form.querySelectorAll('input[type="checkbox"]')) {
      input.disabled = false;
      input.closest("label").classList.remove("is-disabled");
    }
    if (!validPriceRange || !form.checkValidity()) {
      this.setCountLabel(this.dataset.applyLabel);
      return;
    }
    this.setCountLabel(this.dataset.countPending, true);
    this.countTimer = window.setTimeout(() => this.previewCount(), 250);
  }

  async previewCount() {
    const request = new AbortController();
    this.countRequest = request;
    const url = this.formURL();
    url.searchParams.set("section_id", this.dataset.sectionId);
    const timeout = window.setTimeout(() => request.abort(), 10000);
    try {
      const response = await fetch(url, { signal: request.signal });
      if (!response.ok) throw new Error("Count unavailable");
      const result = new DOMParser().parseFromString(await response.text(), "text/html");
      const label = result.querySelector("[data-result-count]")?.dataset.applyCountLabel;
      if (!label) throw new Error("Count missing");
      if (this.countRequest === request && this.dialog.open) {
        this.updateFacetCounts(result);
        this.setCountLabel(label);
      }
    } catch (_error) {
      if (this.countRequest === request && this.dialog.open) this.setCountLabel(this.dataset.applyLabel);
    } finally {
      window.clearTimeout(timeout);
      if (this.countRequest === request) this.countRequest = null;
    }
  }

  captureFilterGroups() {
    return new Map(
      [...this.querySelectorAll("[data-filter-group]")].map((group) => [
        group.dataset.filterGroup,
        { open: group.open, expanded: group.classList.contains("is-expanded") },
      ]),
    );
  }

  restoreFilterGroups(groups) {
    for (const group of this.querySelectorAll("[data-filter-group]")) {
      const previous = groups.get(group.dataset.filterGroup);
      if (previous) {
        group.open = previous.open;
        if (previous.expanded) group.classList.add("is-expanded");
        const more = group.querySelector("[data-filter-more]");
        if (more && group.classList.contains("is-expanded")) {
          more.setAttribute("aria-expanded", "true");
          more.textContent = more.dataset.less;
        }
      }
    }
  }

  showLoading() {
    const results = this.querySelector(".okz-collection__results").getBoundingClientRect();
    const toolbar = this.querySelector(".okz-collection__toolbar").getBoundingClientRect();
    const grid = this.querySelector("[data-collection-grid], .okz-collection__empty").getBoundingClientRect();
    this.loading.style.left = `${results.left + results.width / 2}px`;
    this.loading.style.top = `${Math.min(window.innerHeight - 64, Math.max(toolbar.bottom + 12, grid.top + 12))}px`;
    this.loading.hidden = false;
  }

  async restoreResultsFocus({ scroll, focusId, request }) {
    const count = this.querySelector("[data-result-count]");
    if (scroll) {
      count.focus({ preventScroll: true });
      const results = this.querySelector(".okz-collection__results");
      results.scrollIntoView({ block: "start", behavior: "instant" });
      await new Promise((resolve) => window.requestAnimationFrame(() => window.requestAnimationFrame(resolve)));
      const header = window.document.querySelector("okazo-shell-header");
      const animations = header?.getAnimations({ subtree: true }) || [];
      await Promise.all(
        animations
          .filter((animation) => animation.effect?.getComputedTiming().iterations !== Infinity)
          .map((animation) => animation.finished.catch(() => {})),
      );
      await new Promise(window.requestAnimationFrame);
      if (this.request === request && this.isConnected) results.scrollIntoView({ block: "start", behavior: "instant" });
    } else {
      const target = focusId ? this.querySelector(`#${CSS.escape(focusId)}`) : null;
      (target || count).focus({ preventScroll: true });
    }
  }

  async navigate(destination, { history = true, scroll = false } = {}) {
    const url = new URL(destination, location.href);
    const current = new URL(this.committedURL);
    for (const key of ["preview_theme_id", "preview_key"]) {
      if (current.searchParams.has(key) && !url.searchParams.has(key))
        url.searchParams.set(key, current.searchParams.get(key));
    }
    url.searchParams.delete("section_id");
    this.request?.abort();
    const request = new AbortController();
    this.request = request;
    const focusId = this.contains(document.activeElement) ? document.activeElement.id : null;
    const groups = this.captureFilterGroups();
    const content = this.querySelector("[data-collection-content]");
    const status = this.querySelector("[data-collection-status]");
    const error = this.querySelector("[data-collection-error]");
    error.hidden = true;
    status.textContent = this.dataset.loading;
    content.setAttribute("aria-busy", "true");
    this.showLoading();
    if (this.dialog.open) {
      this.pendingValues = null;
      this.dialog.close();
      this.restoreForm();
    }
    const requestURL = new URL(url);
    requestURL.searchParams.set("section_id", this.dataset.sectionId);
    const timeout = window.setTimeout(() => request.abort("timeout"), 15000);
    try {
      const response = await fetch(requestURL, { signal: request.signal });
      if (!response.ok) throw new Error("Collection request failed");
      const document = new DOMParser().parseFromString(await response.text(), "text/html");
      const replacement = document.querySelector("[data-collection-content]");
      if (!replacement || !replacement.querySelector("[data-result-count]"))
        throw new Error("Collection response is incomplete");
      if (this.request !== request) return;
      content.replaceChildren(...replacement.childNodes);
      this.validatePriceRange();
      this.restoreFilterGroups(groups);
      if (history) window.history.pushState({}, "", url);
      this.committedURL = url.href;
      const count = this.querySelector("[data-result-count]");
      status.textContent = count.textContent;
      status.classList.add("visually-hidden");
      await this.restoreResultsFocus({ scroll, focusId, request });
    } catch (reason) {
      if (this.request !== request || !this.isConnected) return;
      if (request.signal.aborted && request.signal.reason !== "timeout") return;
      this.querySelector("[data-filter-form]")?.reset();
      this.validatePriceRange();
      error.textContent = this.dataset.error;
      error.hidden = false;
      status.textContent = "";
      if (!history) window.history.replaceState({}, "", this.committedURL);
      error.scrollIntoView({ block: "nearest", behavior: "instant" });
    } finally {
      window.clearTimeout(timeout);
      if (this.request === request) {
        content.removeAttribute("aria-busy");
        this.loading.hidden = true;
      }
    }
  }
}

if (!customElements.get("okazo-collection")) customElements.define("okazo-collection", OkazoCollection);
